import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search, User, Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Eternal Shishya Logo SVG (inline, adapted from Logo.tsx)
const EternalShishyaLogo = ({ className = '', size = 32 }) => (
  <svg
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    className={className}
  >
    <path d="M20 5C24 2 30 4 32 10C34 16 30 22 26 26C23 29 21 32 20 35C19 32 17 29 14 26C10 22 6 16 8 10C10 4 16 2 20 5Z" fill="#F9B877" />
    <path d="M20 5C24 2 30 4 32 10C34 16 30 22 26 26C23 29 21 32 20 35C19 32 17 29 14 26C10 22 6 16 8 10C10 4 16 2 20 5Z" fill="url(#leafGradient)" />
    <path d="M20 5C24 2 30 4 32 10C34 16 30 22 26 26C23 29 21 32 20 35C19 32 17 29 14 26C10 22 6 16 8 10C10 4 16 2 20 5Z" stroke="#A67C52" strokeWidth="1.5" fill="none" />
    <path d="M20 5L20 32" stroke="#654733" strokeWidth="1.2" />
    <path d="M20 12C22 14 25 16 27 20" stroke="#7A5639" strokeWidth="0.8" />
    <path d="M20 12C18 14 15 16 13 20" stroke="#7A5639" strokeWidth="0.8" />
    <path d="M20 18C22 20 24 22 25 24" stroke="#956A44" strokeWidth="0.6" />
    <path d="M20 18C18 20 16 22 15 24" stroke="#956A44" strokeWidth="0.6" />
    <text x="20" y="22" textAnchor="middle" style={{ fontSize: '9px', fontWeight: 'bold', fontFamily: 'serif', fill: '#543C2E' }}>ॐ</text>
    <defs>
      <linearGradient id="leafGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F27516" stopOpacity="0.3" />
        <stop offset="50%" stopColor="#A67C52" stopOpacity="0.1" />
        <stop offset="100%" stopColor="#DB6459" stopOpacity="0.2" />
      </linearGradient>
    </defs>
  </svg>
);

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, isAuthenticated, logout } = useAuth();

  const navItems = [
    { name: 'Courses', path: '/' },
    { name: 'Internships', path: '/internships' },
  ];

  if (isAuthenticated) {
    navItems.push({ name: 'Dashboard', path: '/dashboard' });
  }

  // Add admin link if user is admin
  if (isAuthenticated && user?.email === 'admin@eternalcourses.com') {
    navItems.push({ name: 'Admin', path: '/admin' });
  }

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="fixed top-0 left-0 right-0 z-50 bg-earth-900 text-earth-50 border-b border-earth-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Brand */}
          <motion.div whileHover={{ scale: 1.05 }} className="flex items-center space-x-2">
            <Link to="/" className="flex items-center space-x-2">
              <EternalShishyaLogo className="text-saffron-400" size={32} />
              <div>
                <span className="text-xl font-serif font-semibold text-earth-50">Eternal Shishya</span>
                <div className="text-xs text-earth-300 -mt-1">शाश्वत शिष्य</div>
              </div>
            </Link>
          </motion.div>

          {/* Search Bar */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-earth-400" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-earth-800 text-earth-50 placeholder-earth-400 border border-earth-700 focus:border-saffron-400 focus:outline-none transition-all duration-300"
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navItems.map((item) => (
              <motion.div key={item.name} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to={item.path} className="px-4 py-2 rounded-lg text-sm font-medium text-earth-300 hover:text-saffron-400 transition-colors">
                  {item.name}
                </Link>
              </motion.div>
            ))}
            
            {isAuthenticated ? (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-earth-300">
                  Welcome, {user?.first_name}
                </span>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <button 
                    onClick={logout}
                    className="px-3 py-2 rounded-lg bg-earth-800 text-earth-300 hover:text-saffron-400 transition-colors"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </motion.div>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/login" className="px-4 py-2 rounded-lg text-sm font-medium text-earth-300 hover:text-saffron-400 transition-colors">
                    Login
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/register" className="px-4 py-2 rounded-lg bg-saffron-600 text-white hover:bg-saffron-700 transition-colors text-sm font-medium">
                    Sign Up
                  </Link>
                </motion.div>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="px-3 py-2 rounded-lg bg-earth-800 text-earth-300 hover:text-saffron-400 transition-colors"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-earth-900 border-t border-earth-800"
        >
          <div className="px-4 py-4 space-y-2">
            {/* Mobile Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-earth-400" />
              <input
                type="text"
                placeholder="Search courses..."
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-earth-800 text-earth-50 placeholder-earth-400 border border-earth-700 focus:border-saffron-400 focus:outline-none transition-all duration-300"
              />
            </div>
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className="block px-4 py-2 rounded-lg text-center text-earth-300 hover:text-saffron-400 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            {isAuthenticated ? (
              <div className="space-y-2">
                <div className="px-4 py-2 text-sm text-earth-300">
                  Welcome, {user?.first_name}
                </div>
                <button 
                  onClick={logout}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-earth-800 text-earth-300 hover:text-saffron-400 transition-colors"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Link
                  to="/login"
                  className="block px-4 py-2 rounded-lg text-center text-earth-300 hover:text-saffron-400 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block px-4 py-2 rounded-lg text-center bg-saffron-600 text-white hover:bg-saffron-700 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Navbar;
